import { useEffect } from 'react';

/**
 *
 * @param ref The reference to the element we want to click outside to react on
 * @param handler Callback method
 * @param blockHandler Boolean that can control if the handler callback should be called or not
 * @param blockingIds String array of ids. A recursive function checks if the element clicked (target)
 * or any parent of the element has an id included in the array and can prevent the callback to be called
 */
export const useOnClickOutside = (
  ref: any,
  handler: any,
  blockHandler = false,
  blockingIds: string[] = [],
) => {
  useEffect(
    () => {
      const listener = (event: MouseEvent | TouchEvent) => {
        //Check to see if target's id is in blocked list
        const eventTargetAsElement = event.target as HTMLElement;

        const searchForBlocked = (
          targetElement: HTMLElement | null | undefined,
          blockingIds: string[],
        ): boolean => {
          for (const id of blockingIds) {
            if (targetElement && targetElement.id === id) {
              return true;
            }
          }
          // if none of the blockedIds were found, recurse on the parent element
          if (targetElement?.parentElement) {
            return searchForBlocked(targetElement.parentElement, blockingIds);
          }
          // if there are no more parent elements to check, return false
          return false;
        };

        const isBlocked = searchForBlocked(eventTargetAsElement, blockingIds);

        // Do nothing if clicking ref's element or descendent elements
        if (!ref.current || ref.current.contains(event.target) || isBlocked) {
          return;
        }
        if (!blockHandler) handler(event);
      };
      document.addEventListener('mouseup', listener);
      document.addEventListener('touchend', listener);
      return () => {
        document.removeEventListener('mouseup', listener);
        document.removeEventListener('touchend', listener);
      };
    },
    // Add ref and handler to effect dependencies
    // It's worth noting that because passed in handler is a new ...
    // ... function on every render that will cause this effect ...
    // ... callback/cleanup to run every render. It's not a big deal ...
    // ... but to optimize you can wrap handler in useCallback before ...
    // ... passing it into this hook.
    [ref, handler],
  );
};
